import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Chip
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { ApolloConsumer, useQuery } from '@apollo/client';
import AutocompleteSearch from './AutocompleteSearch';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { STATE_SELECTIONS } from '../constants';
import { useIsFeatureFlagEnabled } from '../contexts/feature-flag-context';
import { useUser } from '../contexts/user-context';
import Loader from './Loader';

import { GetBoardSettings } from '../queries/getBoardSettings';

const styles = (theme) => ({
  root: {
    overflow: 'visible'
  },
  divider: {
    height: theme.spacing(2)
  },
  autocomplete: {
    height: 'auto'
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  }
});

const emptyLocationFilter = {
  type: '',
  attr: '',
  values: []
};

const BoardForm = ({
  classes,
  board,
  boardId,
  boardMutation,
  closeHandler
}) => {
  const tabGroupFlag = useIsFeatureFlagEnabled('vip-fe-add-tab-groups');
  const [name, setName] = useState(board.name);
  const [locationFilter, setLocationFilter] = useState(
    board.locationFilter || emptyLocationFilter
  );
  const [returnGigsSetting, setReturnGigsSetting] = useState(
    board.returnGigsSetting
  );
  const [showFirstAndLastName, setShowFirstAndLastName] = useState(
    board.showFirstAndLastName || false
  );
  const [textlineGroupUuid, setTextlineGroupUuid] = useState(
    board.textlineGroupUuid
  );
  const [selectedLabel, setSelectedLabel] = useState(
    board?.labels ? board?.labels?.[0]?.name : 'senders'
  );
  const [profileIds, setProfileIds] = useState(
    board?.profiles?.map((v) => v.id)
  );
  const [masterAccountIds, setMasterAccountIds] = useState(
    board?.masterAccounts?.map((v) => v.id)
  );
  const [userIds, setUserIds] = useState(board?.users?.map((v) => v.id));
  const [groupIds, setGroupIds] = useState(board?.groups?.map((v) => v.id));
  const { hasRoles } = useUser();
  const isUserLabelManager = hasRoles(['dashboard_label_manager']);

  const { loading, error, data } = useQuery(GetBoardSettings, {
    variables: { withLabels: tabGroupFlag }
  });

  if (loading) return <Loader />;
  if (error) return <p>Error: {error.message}</p>;

  const handleNameChange = (event) => {
    setName(event.target.value);
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;

    const stateSetters = {
      return_gigs_setting: setReturnGigsSetting,
      textlineGroupUuid: setTextlineGroupUuid,
      labels: setSelectedLabel
    };

    const setter = stateSetters[name];
    if (setter) {
      setter(value === '' ? undefined : value);
    }
  };

  const handleProfileChange = (value) => {
    setProfileIds(value.map((v) => v.value));
  };

  const handleMasterAccountChange = (value) => {
    setMasterAccountIds(value.map((v) => v.value));
  };

  const handleUserChange = (value) => {
    setUserIds(value.map((v) => v.value));
  };

  const handleGroupChange = (value) => {
    setGroupIds(value.map((v) => v.value));
  };

  const handleLocationFilterChange = (event) => {
    const { name, value } = event.target;
    if (value === '') {
      updateLocationFilter('attr', '');
      updateLocationFilter('values', []);
    }
    updateLocationFilter(name, value);
  };

  const handleLocationFilterValuesChange = (event, value) => {
    updateLocationFilter('values', value);
  };

  const updateLocationFilter = (key, value) => {
    setLocationFilter({ ...locationFilter, [key]: value });
  };

  const filterValuesOptions = () => {
    return locationFilter.attr === 'state' ? STATE_SELECTIONS : [];
  };

  const isAttrFilterDisabled = () => {
    return locationFilter.type === '';
  };

  const isValuesFilterDisabled = () => {
    return locationFilter.type === '' || locationFilter.attr === '';
  };

  const handleCheckboxChange = (event) => {
    setShowFirstAndLastName(event.target.checked);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const updatedLocationFilter =
      locationFilter.type === '' ||
      locationFilter.attr === '' ||
      locationFilter.values.length === 0
        ? null
        : (({ __typename, ...rest }) => rest)(locationFilter);

    boardMutation({
      variables: {
        id: boardId,
        name: name,
        settings: board.settings,
        profileIds: profileIds,
        masterAccountIds: masterAccountIds,
        userIds: userIds,
        groupIds: groupIds,
        locationFilter: updatedLocationFilter,
        returnGigsSetting: returnGigsSetting,
        showFirstAndLastName: showFirstAndLastName,
        textlineGroupUuid:
          textlineGroupUuid == null
            ? data.boardSettings?.textlineChannels?.[0]?.['channel_group_uuid']
            : textlineGroupUuid,
        ...(selectedLabel != null ? { labels: [selectedLabel] } : {})
      }
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle id="form-dialog-title">Edit Board: {board.name}</DialogTitle>
      <DialogContent className={classes.root}>
        <div>
          <TextField
            required
            id="name"
            label="Name"
            margin="normal"
            fullWidth
            autoFocus
            onChange={handleNameChange}
            defaultValue={board.name}
          />
          <div className={classes.divider} />
          <ApolloConsumer>
            {(client) => (
              <div>
                <AutocompleteSearch
                  id="profile_ids"
                  label="Profiles"
                  value={board.profiles}
                  client={client}
                  modelType="Profile"
                  onChange={handleProfileChange}
                  className={classes.autocomplete}
                  showFirstAndLastName={showFirstAndLastName}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showFirstAndLastName}
                      onChange={handleCheckboxChange}
                      color="primary"
                    />
                  }
                  label="Use First and Last Name"
                />
                <div className={classes.divider} />
                <AutocompleteSearch
                  id="master_account_ids"
                  label="Master Accounts"
                  value={board.masterAccounts}
                  client={client}
                  modelType="MasterAccount"
                  onChange={handleMasterAccountChange}
                  className={classes.autocomplete}
                />
              </div>
            )}
          </ApolloConsumer>

          <div className={classes.divider} />
          <Typography variant="h6">Assignments</Typography>
          <div className={classes.divider} />
          <ApolloConsumer>
            {(client) => (
              <div>
                <AutocompleteSearch
                  id="user_ids"
                  label="Users"
                  value={board.users}
                  client={client}
                  modelType="User"
                  onChange={handleUserChange}
                  className={classes.autocomplete}
                />
                <div className={classes.divider} />
                <AutocompleteSearch
                  id="group_ids"
                  label="Groups"
                  value={board.groups}
                  client={client}
                  modelType="Group"
                  onChange={handleGroupChange}
                  className={classes.autocomplete}
                />
              </div>
            )}
          </ApolloConsumer>
        </div>

        <div className={classes.divider} />

        <Typography variant="h6">Filters</Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: '100px' }}>
          <div>
            <Typography variant="subtitle1">Location Filter</Typography>
            <FormControl className={classes.formControl}>
              <InputLabel id="locationFilter.type">Location</InputLabel>
              <Select
                labelId="locationFilter.type"
                id="locationFilter.type"
                name="type"
                value={locationFilter.type}
                onChange={handleLocationFilterChange}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value={'pickup'}>Pickup Location</MenuItem>
                <MenuItem value={'delivery'}>Delivery Location</MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.formControl}>
              <InputLabel id="locationFilter.attr">Attribute</InputLabel>
              <Select
                labelId="locationFilter.attr"
                id="locationFilter.attr"
                name="attr"
                value={locationFilter.attr}
                onChange={handleLocationFilterChange}
                disabled={isAttrFilterDisabled()}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value={'city'}>Cities</MenuItem>
                <MenuItem value={'state'}>States</MenuItem>
                <MenuItem value={'store_number'}>Store Numbers</MenuItem>
              </Select>
            </FormControl>

            <FormControl className={classes.formControl}>
              <Autocomplete
                multiple
                id="locationFilter.values"
                name="values"
                options={filterValuesOptions()}
                value={locationFilter.values}
                freeSolo
                autoSelect
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Values" />
                )}
                onChange={handleLocationFilterValuesChange}
                disabled={isValuesFilterDisabled()}
              />
            </FormControl>
          </div>
          <div>
            <Typography variant="subtitle1">Return Gigs Filter</Typography>
            <FormControl className={classes.formControl}>
              <InputLabel></InputLabel>
              <Select
                id="return_gigs_setting"
                name="return_gigs_setting"
                defaultValue={
                  board.returnGigsSetting != null ? board.returnGigsSetting : ''
                }
                displayEmpty={true}
                onChange={handleSelectChange}
              >
                <MenuItem value={''}>None</MenuItem>
                <MenuItem value={0}>Only Return Gigs</MenuItem>
                <MenuItem value={1}>Exclude Return Gigs</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
        <div className={classes.divider} />
        <Typography variant="h6">Board Settings</Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: '150px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '50px' }}>
            <Typography variant="subtitle1">Select Textline Channel</Typography>
            <FormControl className={classes.formControl}>
              <Select
                id="textlineGroupUuid"
                name="textlineGroupUuid"
                defaultValue={
                  textlineGroupUuid != null
                    ? textlineGroupUuid
                    : data.boardSettings?.textlineChannels?.[0]?.[
                        'channel_group_uuid'
                      ]
                }
                onChange={handleSelectChange}
              >
                {data.boardSettings.textlineChannels.map((option) => (
                  <MenuItem value={option['channel_group_uuid']}>
                    {option['channel_name']}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          {tabGroupFlag && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '50px' }}>
              <Typography variant="subtitle1">Select Tab Group</Typography>
              <FormControl className={classes.formControl}>
                <Select
                  id="labels"
                  name="labels"
                  displayEmpty={true}
                  defaultValue={selectedLabel}
                  onChange={handleSelectChange}
                  disabled={!isUserLabelManager}
                >
                  {data.labels.map((option) => (
                    <MenuItem value={option.name}>
                      {option.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" type="submit">
          Submit
        </Button>
        <Button variant="outlined" onClick={closeHandler}>
          Cancel
        </Button>
      </DialogActions>
    </form>
  );
};

export default withStyles(styles)(BoardForm);
